import { useEffect, useState } from 'react'

import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { useResponsive } from 'hooks/useResponsive'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { FullTrip } from 'tripPlanner/types/common'
import type { PublicFullTrip } from 'tripPlanner/types/publicTrip'
import type { TripTab } from 'tripPlanner/utils/tabs'
import type { CustomModalConfig } from './TripMap'
import TripMap from './TripMap'

// We omit items here and instead get it in the actual component itself since this is
// used in a modal. Modals can only handle static data. Not dynamic data such as items
interface Props {
  title: string
  tab: TripTab
  trip: FullTrip | PublicFullTrip
  canEdit: boolean
  mapContainerClassName?: string
  currentSelectionId?: string
  /**
   * Currently only affects explore items.
   * - If true, the map will use a carousel to display explore item cards.
   * - If false, selecting an explore item will bring up a single card.
   */
  useCardCarousel: boolean
  updateModalConfig?: (value: CustomModalConfig) => void
  isTripLoading?: boolean
}

export function MobileTripMap(props: Props) {
  const { closeModal, goBack } = useModalCallbacks()

  const [match] = useResponsive()

  useEffect(() => {
    if (match.desktop || match.largeDesktop) {
      closeModal()
    }
  }, [match, closeModal])

  // customModalConfig when set will override the default modal attributes
  // when unset, modalAttributes will be used instead.
  const [customModalConfig, setCustomModalConfig] = useState<CustomModalConfig>(
    {
      title: undefined,
      closeModalAction: undefined,
      backButtonAction: undefined,
    },
  )

  const [modalAttributes, setModalAttributes] = useState({
    title: props.title,
    closeModalAction: closeModal,
    backButtonAction: goBack,
  })

  useEffect(() => {
    setModalAttributes({
      title: customModalConfig.title ?? props.title,
      closeModalAction: customModalConfig.closeModalAction ?? closeModal,
      backButtonAction: customModalConfig.backButtonAction ?? goBack,
    })
  }, [closeModal, customModalConfig, goBack, props.title])

  return (
    <>
      <ModalHeader
        title={modalAttributes.title}
        onCloseButtonClick={modalAttributes.closeModalAction}
        onBackButtonClick={modalAttributes.backButtonAction}
      />
      <TripMap
        {...props}
        updateModalConfig={setCustomModalConfig}
      />
    </>
  )
}
